import { useEffect, useState } from "react";
import api from "@/lib/api";
import { X, ChevronLeft, ChevronRight, AlertTriangle, CalendarClock, Flag } from "lucide-react";

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function CalendarView({ onClose }) {
  const now = new Date();
  const [m, setM] = useState(now.getMonth());
  const [y, setY] = useState(now.getFullYear());
  const [holidays, setHolidays] = useState([]);
  const [items, setItems] = useState([]);

  useEffect(() => { api.get(`/holidays?year=${y}`).then((r) => setHolidays(r.data)); }, [y]);
  useEffect(() => { api.get("/tracker").then((r) => setItems(r.data)).catch(() => {}); }, []);

  const holidayMap = {};
  holidays.forEach((h) => { holidayMap[h.date] = h.name; });
  const taskMap = {};
  items.forEach((t) => {
    if (!t.due_date) return;
    const cur = taskMap[t.due_date] || { urgent: 0, upcoming: 0, titles: [] };
    if (t.status === "Urgent") cur.urgent++; else if (t.status !== "Complete") cur.upcoming++;
    cur.titles.push(`${t.title} (${t.client_name})`);
    taskMap[t.due_date] = cur;
  });

  const first = new Date(y, m, 1);
  const startDow = (first.getDay() + 6) % 7; // Mon=0
  const days = new Date(y, m + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= days; d++) cells.push(d);

  const prev = () => { if (m === 0) { setM(11); setY(y - 1); } else setM(m - 1); };
  const next = () => { if (m === 11) { setM(0); setY(y + 1); } else setM(m + 1); };
  const ds = (d) => `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  const todayStr = now.toISOString().slice(0, 10);

  const upcomingHolidays = holidays.filter((h) => h.date >= todayStr).slice(0, 4);

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4" style={{ background: "rgba(17,19,24,0.55)" }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto fade-up" data-testid="calendar-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border-subtle)]">
          <div className="flex items-center gap-3">
            <button onClick={prev} className="p-1.5 rounded-lg hover:bg-[var(--bg-subtle)]"><ChevronLeft className="w-5 h-5" /></button>
            <h3 className="font-serif text-xl w-52 text-center">{MONTHS[m]} {y}</h3>
            <button onClick={next} className="p-1.5 rounded-lg hover:bg-[var(--bg-subtle)]"><ChevronRight className="w-5 h-5" /></button>
          </div>
          <button data-testid="calendar-close" onClick={onClose} className="text-[var(--text-muted)] hover:text-[var(--urgent)]"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-7 gap-1 mb-1">
            {DOW.map((d) => <div key={d} className="text-center text-[10px] font-bold uppercase text-[var(--text-muted)] py-1">{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {cells.map((d, i) => {
              if (!d) return <div key={i} />;
              const key = ds(d);
              const hol = holidayMap[key];
              const tk = taskMap[key];
              const isToday = key === todayStr;
              const isSun = new Date(y, m, d).getDay() === 0;
              return (
                <div key={i} title={[hol, ...(tk ? tk.titles : [])].filter(Boolean).join(" • ")}
                  className={`min-h-[64px] rounded-lg border p-1.5 text-xs ${isToday ? "border-[var(--gold)] ring-1 ring-[var(--gold)]" : "border-[var(--border-subtle)]"} ${hol ? "bg-[var(--gold-light)]" : "bg-white"}`}>
                  <div className={`font-semibold ${isSun || hol ? "text-[var(--urgent)]" : "text-[var(--text-primary)]"}`}>{d}</div>
                  {hol && <div className="text-[9px] leading-tight text-[var(--gold-hover)] line-clamp-2 flex items-center gap-0.5"><Flag className="w-2.5 h-2.5 shrink-0" />{hol}</div>}
                  <div className="flex gap-1 mt-1">
                    {tk?.urgent > 0 && <span className="inline-flex items-center gap-0.5 text-[9px] px-1 rounded" style={{ background: "#FEE2E2", color: "#DC2626" }}><AlertTriangle className="w-2.5 h-2.5" />{tk.urgent}</span>}
                    {tk?.upcoming > 0 && <span className="inline-flex items-center gap-0.5 text-[9px] px-1 rounded" style={{ background: "#DBEAFE", color: "#2563EB" }}><CalendarClock className="w-2.5 h-2.5" />{tk.upcoming}</span>}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex flex-wrap gap-4 mt-4 text-xs text-[var(--text-secondary)]">
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-[var(--gold-light)] border border-[var(--border-gold)]" /> National Holiday</span>
            <span className="flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5 text-[var(--urgent)]" /> Urgent task</span>
            <span className="flex items-center gap-1"><CalendarClock className="w-3.5 h-3.5 text-[#2563EB]" /> Upcoming schedule</span>
          </div>
          {upcomingHolidays.length > 0 && (
            <div className="mt-4 bg-[var(--bg-subtle)] rounded-xl p-3">
              <div className="text-xs font-semibold text-[var(--text-secondary)] mb-1">Upcoming National Holidays</div>
              {upcomingHolidays.map((h) => <div key={h.date} className="text-xs text-[var(--text-primary)]"><span className="font-mono text-[var(--gold-hover)]">{h.date}</span> · {h.name}</div>)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
